// Secure Authentication Service
// Uses Netlify Functions for server-side auth validation

export interface User {
  email: string
  role: string
}

export class SecureAuthService {
  private user: User | null = null
  private listeners: Array<() => void> = []

  /**
   * Login user with email and password
   * Password is validated server-side via Netlify Function
   */
  async login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('SecureAuth: Attempting login for:', email)

      const response = await fetch('/.netlify/functions/admin-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Important: include cookies
        body: JSON.stringify({ email, password })
      })

      const data = await response.json()

      if (!response.ok) {
        console.error('SecureAuth: Login failed:', data.error)
        return {
          success: false,
          error: data.error || 'Invalid email or password'
        }
      }

      console.log('SecureAuth: Login successful')

      // Store user info (not sensitive, just for display)
      this.user = { email, role: 'admin' }
      this.saveUserToStorage(this.user)
      this.notifyListeners()

      return { success: true }
    } catch (error) {
      console.error('SecureAuth: Login error:', error)
      return {
        success: false,
        error: 'Login failed. Please check your connection and try again.'
      }
    }
  }

  /**
   * Verify if user is authenticated
   * Checks JWT token validity server-side
   */
  async verifyAuth(): Promise<boolean> {
    try {
      console.log('SecureAuth: Verifying authentication...')

      const response = await fetch('/.netlify/functions/admin-verify', {
        credentials: 'include' // Include cookies
      })

      if (!response.ok) {
        console.log('SecureAuth: Not authenticated')
        this.user = null
        this.clearUserFromStorage()
        return false
      }

      const data = await response.json()

      if (data.authenticated && data.user) {
        console.log('SecureAuth: Authenticated as:', data.user.email)
        this.user = data.user
        this.saveUserToStorage(this.user)
        return true
      }

      console.log('SecureAuth: Authentication failed')
      this.user = null
      this.clearUserFromStorage()
      return false
    } catch (error) {
      console.error('SecureAuth: Verification error:', error)
      this.user = null
      this.clearUserFromStorage()
      return false
    }
  }

  /**
   * Logout user
   * Clears cookie by setting expired cookie
   */
  async logout(): Promise<void> {
    console.log('SecureAuth: Logging out')

    // Clear cookie by setting it to expired
    document.cookie = 'admin_token=; Max-Age=0; Path=/; Secure; SameSite=Strict'

    // Clear local user state
    this.user = null
    this.clearUserFromStorage()
    this.notifyListeners()
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    // Try to load from storage if not in memory
    if (!this.user) {
      this.user = this.loadUserFromStorage()
    }
    return this.user
  }

  /**
   * Check if user is authenticated (client-side check only)
   * For real validation, use verifyAuth()
   */
  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null
  }

  /**
   * Subscribe to auth state changes
   */
  subscribe(callback: () => void): () => void {
    this.listeners.push(callback)
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback)
    }
  }

  /**
   * Notify all listeners of auth state change
   */
  private notifyListeners(): void {
    this.listeners.forEach(callback => callback())
  }

  /**
   * Save user to localStorage (for display purposes only, not auth)
   */
  private saveUserToStorage(user: User): void {
    try {
      localStorage.setItem('saltymermaid_user', JSON.stringify(user))
    } catch (error) {
      console.error('SecureAuth: Failed to save user to storage:', error)
    }
  }

  /**
   * Load user from localStorage
   */
  private loadUserFromStorage(): User | null {
    try {
      const stored = localStorage.getItem('saltymermaid_user')
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.error('SecureAuth: Failed to load user from storage:', error)
      return null
    }
  }

  /**
   * Clear user from localStorage
   */
  private clearUserFromStorage(): void {
    try {
      localStorage.removeItem('saltymermaid_user')
    } catch (error) {
      console.error('SecureAuth: Failed to clear user from storage:', error)
    }
  }
}

// Export singleton instance
export const secureAuth = new SecureAuthService()
